import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Crown, Target, Zap, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { quizLeadSchema, getValidationError } from "@/lib/validation";
import SkipToContent from "@/components/SkipToContent";

type QuizStep = "hero" | "q1" | "q2" | "q3" | "lead" | "success";

interface Answer {
  q1?: string;
  q2?: string;
  q3?: string;
}

const QuizMenPage = () => {
  const [step, setStep] = useState<QuizStep>("hero");
  const [answers, setAnswers] = useState<Answer>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAnswer = (question: keyof Answer, answer: string) => {
    setAnswers((prev) => ({ ...prev, [question]: answer }));
    
    // Move to next step
    if (question === "q1") setStep("q2");
    if (question === "q2") setStep("q3");
    if (question === "q3") setStep("lead");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Bot detection: if honeypot field is filled, silently reject
    if (honeypot) {
      setStep("success");
      return;
    }
    
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const phone = formData.get("phone") as string;
    const email = formData.get("email") as string;

    // Validate form data with Zod schema
    const validationResult = quizLeadSchema.safeParse({
      name,
      phone,
      email,
      quiz_q1: answers.q1 || null,
      quiz_q2: answers.q2 || null,
      quiz_q3: answers.q3 || null,
    });

    if (!validationResult.success) {
      const errorMessage = getValidationError(validationResult);
      toast({
        title: "שגיאה",
        description: errorMessage || "נא לבדוק את הפרטים שהזנת",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Save validated data to Supabase
      const { error } = await supabase.from("leads").insert({
        name: validationResult.data.name,
        phone: validationResult.data.phone,
        email: validationResult.data.email,
        source_page: "quiz_men",
        quiz_q1: validationResult.data.quiz_q1,
        quiz_q2: validationResult.data.quiz_q2,
        quiz_q3: validationResult.data.quiz_q3,
      });

      if (error) throw error;
      setStep("success");
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשליחת הטופס. נסה שוב.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "hero":
        return (
          <div key="hero" className="text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium opacity-0 animate-fade-in-scale">
              <Crown className="w-4 h-4" />
              <span>מבדק אישיות זוגית</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif text-cream leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              איזה 'טיפוס זוגי' אתה?
            </h1>
            
            <p className="text-xl md:text-2xl text-cream/80 max-w-xl mx-auto leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              גלה את אסטרטגיית הזוגיות שלך ב-60 שניות
              <br />
              <span className="text-gold">(ולמה זה לא עבד עד היום)</span>
            </p>
            
            <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.45s' }}>
              <Button
                onClick={() => setStep("q1")}
                className="h-16 px-12 text-xl font-bold bg-gold hover:bg-gold-light text-navy btn-glow transition-all duration-300 hover:scale-105"
              >
                התחל מבדק
                <ArrowLeft className="mr-2 w-6 h-6" />
              </Button>
            </div>
            
            <p className="text-cream/50 text-sm opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              ⏱️ 60 שניות בלבד • 🔒 100% דיסקרטי
            </p>
          </div>
        );

      case "q1":
        return (
          <QuestionScreen
            key="q1"
            questionNumber={1}
            totalQuestions={3}
            question="מה הדבר שהכי מתסכל אותך באפליקציות היום?"
            icon={<Target className="w-8 h-8 text-gold" />}
            options={[
              { id: "a", text: "בזבוז הזמן (התכתבויות ארוכות שנגמרות בלא כלום)" },
              { id: "b", text: "המשחקים (נשים לא רציניות או נעלמות)" },
              { id: "c", text: "חוסר האיכות (קשה למצוא מישהי שיש בה גם תוכן וגם משיכה)" },
            ]}
            onSelect={(answer) => handleAnswer("q1", answer)}
          />
        );

      case "q2":
        return (
          <QuestionScreen
            key="q2"
            questionNumber={2}
            totalQuestions={3}
            question="איך אתה מתנהל כשיש אי-הסכמה בקשר?"
            icon={<Zap className="w-8 h-8 text-gold" />}
            options={[
              { id: "a", text: 'המנכ"ל - חותך עניינים, רוצה פתרון עכשיו.' },
              { id: "b", text: "הדיפלומט - מנסה להבין ולהתפשר." },
              { id: "c", text: "הנמנע - צריך שקט, מחכה שהסערה תחלוף." },
            ]}
            onSelect={(answer) => handleAnswer("q2", answer)}
          />
        );

      case "q3":
        return (
          <QuestionScreen
            key="q3"
            questionNumber={3}
            totalQuestions={3}
            question="תכל'ס, מה אתה מחפש כרגע?"
            icon={<Crown className="w-8 h-8 text-gold" />}
            options={[
              { id: "a", text: "שותפה לדרך - לרוץ ביחד קריירה ומשפחה." },
              { id: "b", text: "השקט שאחרי הסערה - בית רגוע לחזור אליו." },
              { id: "c", text: "הניצוץ - מישהי שתחזיר לי את הריגוש." },
            ]}
            onSelect={(answer) => handleAnswer("q3", answer)}
          />
        );

      case "lead":
        return (
          <div key="lead" className="text-center space-y-8 max-w-md mx-auto">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium opacity-0 animate-fade-in-scale">
              <CheckCircle2 className="w-4 h-4" />
              <span>הניתוח שלך מוכן</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-serif text-cream leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              האלגוריתם זיהה את דפוס הזוגיות שלך
            </h2>
            
            <p className="text-lg text-cream/80 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              הזן פרטים לקבלת התוצאה והתאמה ראשונית
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="source" value="quiz_men" />
              {/* Honeypot field - hidden from users, bots will fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                aria-hidden="true"
              />
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                <label htmlFor="quiz-men-name" className="sr-only">שם מלא</label>
                <Input
                  id="quiz-men-name"
                  type="text"
                  name="name"
                  placeholder="שם מלא"
                  required
                  maxLength={100}
                  aria-required="true"
                  className="bg-navy-light/50 border-gold/30 text-cream placeholder:text-cream/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.4s' }}>
                <label htmlFor="quiz-men-phone" className="sr-only">מספר טלפון</label>
                <Input
                  id="quiz-men-phone"
                  type="tel"
                  name="phone"
                  placeholder="מספר טלפון"
                  required
                  maxLength={12}
                  aria-required="true"
                  className="bg-navy-light/50 border-gold/30 text-cream placeholder:text-cream/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.5s' }}>
                <label htmlFor="quiz-men-email" className="sr-only">כתובת אימייל</label>
                <Input
                  id="quiz-men-email"
                  type="email"
                  name="email"
                  placeholder="כתובת אימייל"
                  required
                  maxLength={255}
                  aria-required="true"
                  className="bg-navy-light/50 border-gold/30 text-cream placeholder:text-cream/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-16 text-xl font-bold bg-gold hover:bg-gold-light text-navy btn-glow transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? "שולח..." : "גלה את התוצאות שלי"}
                </Button>
              </div>
            </form>
            
            <p className="text-cream/50 text-xs opacity-0 animate-fade-up" style={{ animationDelay: '0.7s' }}>
              🔒 הפרטים שלך מוגנים ולא יועברו לצד שלישי
            </p>
          </div>
        );

      case "success":
        return (
          <div key="success" className="text-center space-y-8">
            <div className="w-24 h-24 bg-gold/20 rounded-full flex items-center justify-center mx-auto success-icon success-glow">
              <CheckCircle2 className="w-12 h-12 text-gold" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-serif text-cream leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              מעולה! הניתוח שלך נשלח לעיבוד.
            </h2>
            
            <div className="text-lg text-cream/80 max-w-lg mx-auto opacity-0 animate-fade-up text-right space-y-4" style={{ animationDelay: '0.4s' }}>
              <p>האלגוריתם שלנו מנתח כעת את התשובות שלך.</p>
              <p>התוצאות המלאות וניתוח הטיפוס שלך נשלחו אליך למייל.</p>
            </div>
            
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-6 max-w-lg mx-auto opacity-0 animate-fade-in-scale text-right" style={{ animationDelay: '0.5s' }}>
              <p className="text-gold font-medium text-lg mb-3">
                מה קורה עכשיו?
              </p>
              <p className="text-cream/80 leading-relaxed">
                כדי לשמור על איכות המאגר, הכניסה אליו מותנית ב<strong className="text-cream">שיחת איפיון ובדיקת התאמה.</strong>
              </p>
              <p className="text-cream/80 leading-relaxed mt-3">
                אחד מהמאמנים שלנו ייצור איתך קשר בקרוב כדי לבחון את התאמתך לתהליך ולקבלת גישה למאגר.
              </p>
              <p className="text-cream/80 leading-relaxed mt-3">
                בינתיים, שריינו לך עדיפות בתור לשיחה.
              </p>
            </div>
            
            {/* 50/30/20 Model Explanation */}
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-6 max-w-lg mx-auto opacity-0 animate-fade-in-scale text-right" style={{ animationDelay: '0.55s' }}>
              <p className="text-gold font-medium text-lg mb-3">
                למה השיטה שלנו מצליחה איפה שטינדר נכשל?
              </p>
              <p className="text-cream/80 leading-relaxed">
                אפליקציות בנויות על כימיה ויזואלית (משיכה רגעית). זה מחזיק שבועיים.
              </p>
              <p className="text-cream/80 leading-relaxed mt-3">
                המערכת שלנו בנויה על מודל ה-50/30/20:
              </p>
              <ul className="text-cream/80 leading-relaxed mt-2 space-y-1 pr-4">
                <li><strong className="text-gold">50%</strong> התאמה רגשית (ניהול כעסים, אנרגיה בבית)</li>
                <li><strong className="text-gold">30%</strong> ערכים (כסף, משפחה, דת)</li>
                <li><strong className="text-gold">20%</strong> משיכה והעדפות</li>
              </ul>
              <p className="text-cream/80 leading-relaxed mt-3">
                בשיחת ההתאמה שנקיים, אנחנו לא נשאל אותך איך הצד השני צריך להיראות, אלא נבדוק את ה-50% הקריטיים שהפילו לך את הקשרים הקודמים.
              </p>
            </div>
            
            <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="border-gold/50 text-gold hover:bg-gold/10 h-14 px-8 text-lg"
              >
                חזרה לדף הבית
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>מבדק טיפוס זוגי לגברים | אמון</title>
        <meta
          name="description"
          content="גלה את אסטרטגיית הזוגיות שלך ב-60 שניות. מבדק אישיות זוגית מותאם לגברים שרוצים להבין למה זה לא עבד עד היום."
        />
      </Helmet>
      <SkipToContent />
      <main id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-navy relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light/50 to-navy" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        
        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-2xl">
            {renderStep()}
          </div>
        </div>
        
        {/* Progress bar */}
        {step !== "hero" && step !== "success" && (
          <div 
            className="fixed bottom-0 left-0 right-0 h-1 bg-navy-light"
            role="progressbar"
            aria-valuenow={step === "q1" ? 25 : step === "q2" ? 50 : step === "q3" ? 75 : 100}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="התקדמות במבדק"
          >
            <div
              className="h-full bg-gold transition-all duration-500"
              style={{
                width:
                  step === "q1"
                    ? "25%"
                    : step === "q2"
                    ? "50%"
                    : step === "q3"
                    ? "75%"
                    : "100%",
              }}
            />
          </div>
        )}
      </main>
    </>
  );
};

interface QuestionScreenProps {
  questionNumber: number;
  totalQuestions: number;
  question: string;
  icon: React.ReactNode;
  options: { id: string; text: string }[];
  onSelect: (answer: string) => void;
}

const QuestionScreen = ({
  questionNumber,
  totalQuestions,
  question,
  icon,
  options,
  onSelect,
}: QuestionScreenProps) => {
  return (
    <div className="space-y-8">
      {/* Question header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2 text-cream/60 text-sm opacity-0 animate-fade-up">
          <span>שאלה {questionNumber} מתוך {totalQuestions}</span>
        </div>
        <div className="flex justify-center icon-bounce">{icon}</div>
        <h2 className="text-2xl md:text-3xl font-serif text-cream leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
          {question}
        </h2>
      </div>

      {/* Options with staggered animation */}
      <div className="space-y-4">
        {options.map((option, index) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.text)}
            className={`stagger-item stagger-${index + 1} w-full text-right p-6 bg-navy-light/50 hover:bg-navy-light border border-gold/20 hover:border-gold/50 rounded-xl text-cream transition-all duration-300 hover:scale-[1.02] hover:shadow-gold group`}
          >
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-bold group-hover:bg-gold group-hover:text-navy transition-colors">
                {option.id.toUpperCase()}
              </span>
              <span className="text-lg leading-relaxed">{option.text}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuizMenPage;